import React from 'react';
import { Plus, Trash2, Copy } from 'lucide-react';
import { t } from '../i18n';
import { Button, Input, Select, Alert } from './ui';
import { DAY_KEYS, dayKeyOf, findProblem, minutesOf, sessionsInBlock } from '../lib/availability';

/**
 * A professional's week, in blocks of hours by sede (migration 0044).
 *
 * This drives the booking grid: available_slots makes every time a patient can
 * book out of these blocks, and says where each one is. A block is at one sede,
 * at that sede and also online, or online only. Two blocks on the same day
 * cannot overlap — a professional is in one place at a time — but they can be
 * back to back, which is how a morning at one sede and an afternoon at another
 * is said.
 *
 * Week starts on Monday; blocks carry the ISO weekday (1 = Monday).
 */

const ONLINE_ONLY = 'online-only';

let lastKey = 0;
/** A stable React key: the row's id once saved, a local one before. */
export const withKey = (block) => ({ ...block, key: block.key ?? block.id ?? `new-${(lastKey += 1)}` });

const clock = (minutes) => `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`;

const AvailabilityEditor = ({ blocks, sedes, hasOnline, sessionMinutes, onChange, onNotice }) => {
  const update = (key, patch) => onChange(blocks.map((block) => (block.key === key ? { ...block, ...patch } : block)));
  const remove = (key) => onChange(blocks.filter((block) => block.key !== key));

  // A new block starts where the day's last one ends, so adding the afternoon
  // after the morning needs no retyping.
  const add = (weekday) => {
    const ends = blocks.filter((block) => block.weekday === weekday).map((block) => minutesOf(block.ends_at));
    const start = ends.length ? Math.min(Math.max(...ends), 23 * 60) : 9 * 60;
    onChange([...blocks, withKey({
      weekday,
      starts_at: clock(start),
      ends_at: clock(Math.min(start + 4 * 60, 23 * 60 + 59)),
      location_id: sedes[0]?.id ?? null,
      allows_online: !sedes.length && hasOnline,
      requires_approval: false,
    })]);
  };

  /** Seven days of typing is tedious when most of them are the same. */
  const copyDay = (weekday) => {
    const source = blocks.filter((block) => block.weekday === weekday);
    const targets = [...new Set(blocks.map((block) => block.weekday))].filter((day) => day !== weekday);
    const kept = blocks.filter((block) => !targets.includes(block.weekday));
    const copies = targets.flatMap((day) => source.map(({ key, id, ...rest }) => withKey({ ...rest, weekday: day })));
    onChange([...kept, ...copies]);
    onNotice?.(t('availability.copied'));
  };

  const problem = findProblem(blocks);
  const openDays = new Set(blocks.map((block) => block.weekday));

  return (
    <div className="space-y-3">
      {!sedes.length && <Alert tone="warning">{t('availability.noSedes')}</Alert>}

      <div className="divide-y divide-slate-100">
        {DAY_KEYS.map((dayKey, index) => {
          const weekday = index + 1;
          const dayBlocks = blocks
            .filter((block) => block.weekday === weekday)
            .sort((a, b) => minutesOf(a.starts_at) - minutesOf(b.starts_at));
          return (
            <div key={dayKey} className="py-3">
              <div className="flex flex-wrap items-center gap-2">
                <span className={`w-28 text-sm font-medium ${dayBlocks.length ? 'text-slate-900' : 'text-slate-400'}`}>
                  {t(`weekdays.${dayKey}`)}
                </span>
                {!dayBlocks.length && <span className="text-sm text-slate-400">{t('availability.closed')}</span>}
                <div className="ml-auto flex items-center gap-3">
                  {dayBlocks.length > 0 && openDays.size > 1 && (
                    <button type="button" onClick={() => copyDay(weekday)}
                      className="flex items-center gap-1 text-xs font-medium text-indigo-600 hover:underline">
                      <Copy size={12} />{t('availability.copyDay')}
                    </button>
                  )}
                  {/* A button, not a link: it is the only way to put hours on a
                      day, and small blue text is easy to miss (12 Sep 2026). */}
                  <Button type="button" variant="secondary" onClick={() => add(weekday)}>
                    <Plus size={14} />{t('availability.addBlock')}
                  </Button>
                </div>
              </div>

              {dayBlocks.length > 0 && (
                <ul className="mt-2 space-y-2">
                  {dayBlocks.map((block) => {
                    const sessions = sessionsInBlock(block, sessionMinutes);
                    const known = !block.location_id || sedes.some((sede) => sede.id === block.location_id);
                    return (
                      <li key={block.key} className="flex flex-wrap items-center gap-2 rounded-lg bg-slate-50 px-3 py-2">
                        <Input
                          type="time"
                          value={String(block.starts_at).slice(0, 5)}
                          onChange={(event) => update(block.key, { starts_at: event.target.value })}
                          className="w-28"
                          aria-label={`${t('profile.hoursFrom')} ${t(`weekdays.${dayKey}`)}`}
                        />
                        <Input
                          type="time"
                          value={String(block.ends_at).slice(0, 5)}
                          onChange={(event) => update(block.key, { ends_at: event.target.value })}
                          className="w-28"
                          aria-label={`${t('profile.hoursTo')} ${t(`weekdays.${dayKey}`)}`}
                        />
                        <Select
                          value={block.location_id ?? ONLINE_ONLY}
                          onChange={(event) => update(block.key, event.target.value === ONLINE_ONLY
                            ? { location_id: null, allows_online: true }
                            : { location_id: event.target.value })}
                          className="w-56"
                          aria-label={t('availability.place')}
                        >
                          {sedes.map((sede) => <option key={sede.id} value={sede.id}>{sede.name}</option>)}
                          {/* A sede closed since still shows, so the professional
                              can see why these hours offer nothing. */}
                          {!known && <option value={block.location_id}>{t('availability.inactiveSede')}</option>}
                          {(hasOnline || !block.location_id) && (
                            <option value={ONLINE_ONLY}>{t('availability.onlineOnly')}</option>
                          )}
                        </Select>
                        {block.location_id && hasOnline && (
                          <label className="flex items-center gap-1.5 text-sm text-slate-700">
                            <input
                              type="checkbox"
                              checked={Boolean(block.allows_online)}
                              onChange={(event) => update(block.key, { allows_online: event.target.checked })}
                            />
                            {t('availability.alsoOnline')}
                          </label>
                        )}
                        {/* Hours the professional may have but wants to check
                            that week: a patient's booking waits for them (0055). */}
                        <label className="flex items-center gap-1.5 text-sm text-slate-700">
                          <input
                            type="checkbox"
                            checked={Boolean(block.requires_approval)}
                            onChange={(event) => update(block.key, { requires_approval: event.target.checked })}
                          />
                          {t('availability.requiresApproval')}
                        </label>
                        {/* What those times buy, at this session length. */}
                        <span className="text-xs text-slate-500">
                          {sessions > 0 ? t('availability.sessions', { count: sessions }) : '—'}
                        </span>
                        <button type="button" onClick={() => remove(block.key)} title={t('availability.removeBlock')}
                          className="ml-auto rounded p-1 text-slate-400 hover:bg-white hover:text-rose-600">
                          <Trash2 size={14} />
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          );
        })}
      </div>

      {problem && (
        <Alert tone="danger">
          {t(`availability.problems.${problem.problem}`, { day: t(`weekdays.${dayKeyOf(problem.weekday)}`).toLowerCase() })}
        </Alert>
      )}
      {blocks.length === 0 && <Alert tone="warning">{t('profile.hoursNoneOpen')}</Alert>}
    </div>
  );
};

export default AvailabilityEditor;
